import React, { useState, useRef, useEffect } from 'react';
import { auth, db } from '../firebase';
import { createUserWithEmailAndPassword, updateProfile, sendEmailVerification, signInWithPopup, GoogleAuthProvider, FacebookAuthProvider } from 'firebase/auth';
import { ref, set, get, remove, runTransaction } from 'firebase/database';
import CryptoJS from 'crypto-js';
import { Mail, Lock, User, Phone, ArrowLeft, ArrowRight, ShieldCheck, Eye, EyeOff, Info } from 'lucide-react';
import logo from '../assets/ResortConnectLogo.png';
import * as faceapi from 'face-api.js';
import { sendWelcomeEmail } from '../services/emailService';

const Register = ({ onBackToLogin, onGoHome, isCompletingSocial = false, socialUser = null }) => {
  const [formData, setFormData] = useState(() => {
    const saved = sessionStorage.getItem('registerDraft');
    if (saved && !isCompletingSocial) return JSON.parse(saved);
    return {
      firstName: socialUser?.displayName ? socialUser.displayName.split(' ')[0] : '',
      middleName: '',
      lastName: socialUser?.displayName?.split(' ').length > 1 ? socialUser.displayName.split(' ').slice(1).join(' ') : '',
      email: socialUser?.email || '',
      phoneNumber: socialUser?.phoneNumber || '',
      password: '',
      confirmPassword: '',
      idType: '',
      otherIdType: ''
    };
  });
  const [idImageFile, setIdImageFile] = useState(null);
  const [idImageUrl, setIdImageUrl] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [selfieImageFile, setSelfieImageFile] = useState(null);
  const [selfieImageUrl, setSelfieImageUrl] = useState(null);
  const [isUploadingSelfie, setIsUploadingSelfie] = useState(false);
  const [verifiedFirstName, setVerifiedFirstName] = useState(null);
  const [verifiedMiddleName, setVerifiedMiddleName] = useState(null);
  const [verifiedLastName, setVerifiedLastName] = useState(null);
  const [unverifiedIdPreview, setUnverifiedIdPreview] = useState(null);
  const [idHash, setIdHash] = useState(null);
  const [extractedIdNumber, setExtractedIdNumber] = useState(null);
  const isUploadingRef = useRef(false);
  const isUploadingSelfieRef = useRef(false);
  const [isAutoVerified, setIsAutoVerified] = useState(false);
  const [step, setStep] = useState(() => {
    const saved = sessionStorage.getItem('registerStep');
    return saved && !isCompletingSocial ? parseInt(saved, 10) : 1;
  });

  // Warm-up backend verification server on registration component mount
  useEffect(() => {
    fetch('https://walk-versus-peculiar.ngrok-free.dev/health', {
      headers: { 'ngrok-skip-browser-warning': '69420' }
    }).catch(err => console.log('Warm-up health check notice:', err));
  }, []);

  useEffect(() => {
    if (!isCompletingSocial) {
      sessionStorage.setItem('registerDraft', JSON.stringify(formData));
    }
  }, [formData, isCompletingSocial]);

  useEffect(() => {
    if (!isCompletingSocial) {
      sessionStorage.setItem('registerStep', step.toString());
    }
  }, [step, isCompletingSocial]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showWebcam, setShowWebcam] = useState(false);

  // Face Detection States
  const [isModelLoading, setIsModelLoading] = useState(false);
  const [faceDetectionStatus, setFaceDetectionStatus] = useState("Loading AI models...");
  const [showManualCaptureBtn, setShowManualCaptureBtn] = useState(false);
  const detectInterval = useRef(null);
  const blinkFramesRef = useRef(0);
  const steadyFramesRef = useRef(0);
  const manualTimerRef = useRef(null);
  const timeoutTimerRef = useRef(null);
  const isDetectingRef = useRef(false);
  const hasCapturedRef = useRef(false);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    return () => stopWebcam(); // Cleanup on unmount
  }, []);

  const loadModels = async () => {
    if (isModelLoading) return;
    setIsModelLoading(true);
    setFaceDetectionStatus("Loading AI models (0%)...");
    try {
      await faceapi.nets.tinyFaceDetector.loadFromUri('/models');
      setFaceDetectionStatus("Loading AI models (50%)...");
      await faceapi.nets.faceLandmark68Net.loadFromUri('/models');
      setFaceDetectionStatus("AI models loaded!");
    } catch (err) {
      console.error("Error loading face-api models:", err);
      setFaceDetectionStatus("Error loading face tracking models.");
    } finally {
      setIsModelLoading(false);
    }
  };

  const getEAR = (eye) => {
    const d1 = Math.hypot(eye[1].x - eye[5].x, eye[1].y - eye[5].y);
    const d2 = Math.hypot(eye[2].x - eye[4].x, eye[2].y - eye[4].y);
    const d3 = Math.hypot(eye[0].x - eye[3].x, eye[0].y - eye[3].y);
    return (d1 + d2) / (2.0 * d3);
  };

  const startFaceTracking = () => {
    if (detectInterval.current) clearInterval(detectInterval.current);
    if (manualTimerRef.current) clearTimeout(manualTimerRef.current);
    if (timeoutTimerRef.current) clearTimeout(timeoutTimerRef.current);
    
    blinkFramesRef.current = 0;
    steadyFramesRef.current = 0;
    hasCapturedRef.current = false;
    setShowManualCaptureBtn(false);

    // After 8 seconds without capture, expose manual Take Photo button
    manualTimerRef.current = setTimeout(() => {
      setShowManualCaptureBtn(true);
    }, 8000);

    // Generous 22-second timeout
    timeoutTimerRef.current = setTimeout(() => {
      setShowManualCaptureBtn(true);
      setFaceDetectionStatus("Tap 'Take Photo' to continue.");
    }, 22000);

    detectInterval.current = setInterval(async () => {
      if (!videoRef.current || isDetectingRef.current || hasCapturedRef.current) return;
      isDetectingRef.current = true;
      try {
        const detections = await faceapi.detectSingleFace(
          videoRef.current,
          new faceapi.TinyFaceDetectorOptions({ inputSize: 224, scoreThreshold: 0.25 })
        ).withFaceLandmarks();

        if (detections && !hasCapturedRef.current) {
          const box = detections.detection?.box;
          const boxHeight = box ? box.height : 0;
          const videoHeight = videoRef.current.videoHeight || 1;
          const heightRatio = boxHeight / videoHeight;

          const landmarks = detections.landmarks;
          const leftEye = landmarks.getLeftEye();
          const rightEye = landmarks.getRightEye();

          const leftEAR = getEAR(leftEye);
          const rightEAR = getEAR(rightEye);
          const avgEAR = (leftEAR + rightEAR) / 2.0;

          // Check if face is reasonable size and within view
          const isGoodSize = heightRatio >= 0.15 && heightRatio <= 0.85;

          if (isGoodSize) {
            steadyFramesRef.current++;

            if (steadyFramesRef.current >= 2) {
              setFaceDetectionStatus("Hold still... Capturing");
            } else {
              setFaceDetectionStatus("Face detected! Hold still");
            }

            // Steady face hold for ~1 second (5-6 frames at 150ms interval) -> Auto-Capture!
            if (steadyFramesRef.current >= 5) {
              hasCapturedRef.current = true;
              setFaceDetectionStatus("Capturing selfie...");
              clearInterval(detectInterval.current);
              setTimeout(() => {
                captureWebcam();
              }, 150);
              return;
            }
          } else {
            steadyFramesRef.current = Math.max(0, steadyFramesRef.current - 1);
            if (heightRatio < 0.15) {
              setFaceDetectionStatus("Move closer to the camera");
            } else {
              setFaceDetectionStatus("Move slightly back");
            }
          }

          // Optional instant auto-capture if user blinks
          if (avgEAR < 0.35) {
            blinkFramesRef.current++;
          } else {
            if (blinkFramesRef.current >= 1 && !hasCapturedRef.current) {
              hasCapturedRef.current = true;
              setFaceDetectionStatus("Blink detected! Capturing...");
              clearInterval(detectInterval.current);
              setTimeout(() => {
                captureWebcam();
              }, 150);
              return;
            }
            blinkFramesRef.current = 0;
          }
        } else if (!hasCapturedRef.current) {
          steadyFramesRef.current = 0;
          setFaceDetectionStatus("Face not detected. Position your face in view.");
        }
      } catch (e) {
        console.error("Detection error:", e);
      } finally {
        isDetectingRef.current = false;
      }
    }, 150);
  };

  const startWebcam = async () => {
    setShowWebcam(true);
    await loadModels();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          setFaceDetectionStatus("Position your face in the circle...");
          startFaceTracking();
        };
      }
    } catch (err) {
      console.error("Error accessing webcam:", err);
      alert("Could not access webcam. Please ensure you have granted permission.");
      setShowWebcam(false);
    }
  };

  const stopWebcam = () => {
    if (detectInterval.current) clearInterval(detectInterval.current);
    if (manualTimerRef.current) clearTimeout(manualTimerRef.current);
    if (timeoutTimerRef.current) clearTimeout(timeoutTimerRef.current);
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject;
      const tracks = stream.getTracks();
      tracks.forEach(track => track.stop());
    }
    setShowWebcam(false);
  };


  const captureWebcam = () => {
    if (videoRef.current && canvasRef.current) {
      const context = canvasRef.current.getContext('2d');
      canvasRef.current.width = videoRef.current.videoWidth;
      canvasRef.current.height = videoRef.current.videoHeight;
      context.drawImage(videoRef.current, 0, 0, canvasRef.current.width, canvasRef.current.height);

      canvasRef.current.toBlob((blob) => {
        const file = new File([blob], "selfie.jpg", { type: "image/jpeg" });
        setSelfieImageFile(file);
        setIsAutoVerified(true);
        uploadSelfieImage(file);
        stopWebcam();
      }, 'image/jpeg');
    }
  };

  const idTypes = [
    'Philippine National ID (PhilSys)',
    'Passport',
    "Driver's License",
    "Voter's ID",
    'SSS / GSIS ID',
    'PRC ID',
    'Senior Citizen ID',
    'Postal ID',
    'Other'
  ];

  const validate = () => {
    const { firstName, lastName, email, phoneNumber, password, confirmPassword } = formData;
    const newErrors = {};

    if (!firstName || !firstName.trim()) newErrors.firstName = 'First Name is required';
    if (!lastName || !lastName.trim()) newErrors.lastName = 'Last Name is required';
    if (!email || !email.trim()) newErrors.email = 'Email is required';
    if (!phoneNumber || !phoneNumber.trim()) newErrors.phoneNumber = 'Phone Number is required';
    if (!isCompletingSocial && (!password || !password.trim())) newErrors.password = 'Password is required';
    if (!isCompletingSocial && (!confirmPassword || !confirmPassword.trim())) newErrors.confirmPassword = 'Confirm Password is required';

    const nameRegex = /^[a-zA-Z\s]+$/;
    if (firstName) {
      const fname = firstName.trim();
      if (!nameRegex.test(fname)) newErrors.firstName = 'No special characters allowed.';
      if (fname.length < 2) newErrors.firstName = 'Minimum length is 2 characters.';
      if (fname.split(' ').length > 4) newErrors.firstName = 'Maximum of 4 words allowed.';
    }
    if (formData.middleName) {
      const mname = formData.middleName.trim();
      if (!nameRegex.test(mname)) newErrors.middleName = 'No special characters allowed.';
      if (mname.split(' ').length > 4) newErrors.middleName = 'Maximum of 4 words allowed.';
    }
    if (lastName) {
      const lname = lastName.trim();
      if (!nameRegex.test(lname)) newErrors.lastName = 'No special characters allowed.';
      if (lname.length < 2) newErrors.lastName = 'Minimum length is 2 characters.';
      if (lname.split(' ').length > 4) newErrors.lastName = 'Maximum of 4 words allowed.';
    }

    const emailRegex = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/;
    if (email && !emailRegex.test(email)) newErrors.email = 'Enter a valid email address';

    if (phoneNumber && (phoneNumber.length !== 11 || !phoneNumber.startsWith('09'))) {
      newErrors.phoneNumber = 'Phone number must be 11 digits and start with 09';
    }

    if (!isCompletingSocial && password) {
      const missing = [];
      if (password.length < 8) missing.push('8 characters');
      if (!/[A-Z]/.test(password)) missing.push('an uppercase letter');
      if (!/[a-z]/.test(password)) missing.push('a lowercase letter');
      if (!/[0-9]/.test(password)) missing.push('a number');
      if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) missing.push('a special character');

      if (missing.length > 0) {
        newErrors.password = `Password must contain at least: ${missing.join(', ')}`;
      }
    }

    if (!isCompletingSocial && confirmPassword && password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (step === 2) {
      if (!idImageUrl) newErrors.idImage = 'Please upload a valid ID photo';
      if (!selfieImageUrl) newErrors.selfieImage = 'Please upload a selfie photo';
    }

    return Object.keys(newErrors).length > 0 ? newErrors : null;
  };

  const generateCustomId = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let id = '';
    for (let i = 0; i < 6; i++) {
      id += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return `RC-${id}`;
  };

  const handleEmojiFilter = (value) => {
    // Regex for various emoji ranges
    const emojiRegex = /[\u{1f300}-\u{1f5ff}\u{1f600}-\u{1f64f}\u{1f680}-\u{1f6ff}\u{1f1e6}-\u{1f1ff}\u{2700}-\u{27bf}\u{1f900}-\u{1f9ff}\u{1f3fb}-\u{1f3ff}\u{2600}-\u{26ff}\u{1f100}-\u{1f1ff}]/gu;
    return value.replace(emojiRegex, '');
  };

  const handleNameChange = (field, value) => {
    const cleanValue = handleEmojiFilter(value);
    setFormData({ ...formData, [field]: cleanValue });
    setErrors({ ...errors, [field]: null });

    if (idImageUrl || selfieImageUrl) {
      setIdImageUrl(null);
      setSelfieImageUrl(null);
      setVerifiedFirstName(null);
      setVerifiedMiddleName(null);
      setVerifiedLastName(null);
      setIsAutoVerified(false);
      setShowWebcam(false);
      if (videoRef.current && videoRef.current.srcObject) {
        videoRef.current.srcObject.getTracks().forEach(track => track.stop());
      }
    }
  };

  // Helper: resize & compress image file using HTML Canvas to max 1280px at 0.8 JPEG quality
  const compressImage = async (file) => {
    return new Promise((resolve) => {
      try {
        const reader = new FileReader();
        reader.onload = (e) => {
          const img = new Image();
          img.onload = () => {
            const maxDim = 1280;
            let width = img.width;
            let height = img.height;
            if (width > maxDim || height > maxDim) {
              if (width > height) {
                height = Math.round((height * maxDim) / width);
                width = maxDim;
              } else {
                width = Math.round((width * maxDim) / height);
                height = maxDim;
              }
            }
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);
            canvas.toBlob((blob) => {
              if (blob) {
                resolve(new File([blob], file.name || 'image.jpg', { type: 'image/jpeg' }));
              } else {
                resolve(file);
              }
            }, 'image/jpeg', 0.8);
          };
          img.onerror = () => resolve(file);
          img.src = e.target.result;
        };
        reader.onerror = () => resolve(file);
        reader.readAsDataURL(file);
      } catch (err) {
        console.warn('Canvas compression fallback:', err);
        resolve(file);
      }
    });
  };

  const uploadIdImage = async (file) => {
    if (!file) return;
    if (isUploadingRef.current) return;
    isUploadingRef.current = true;
    setIsUploading(true);
    setErrors({ ...errors, idImage: null });
    
    // Save unverified preview for immediate visual feedback
    const previewUrl = URL.createObjectURL(file);
    setUnverifiedIdPreview(previewUrl);

    // Compress image before sending
    const processedFile = await compressImage(file);
    const startTime = performance.now();
    
    // Check OCR match first with up to 2 attempts
    let ocrData = null;
    let lastError = null;

    for (let attempt = 1; attempt <= 2; attempt++) {
      try {
        const ocrFd = new FormData();
        ocrFd.append('image', processedFile);
        ocrFd.append('firstName', formData.firstName || '');
        ocrFd.append('middleName', formData.middleName || '');
        ocrFd.append('lastName', formData.lastName || '');
        ocrFd.append('idType', (formData.idType || 'Government ID').trim());
        
        console.log(`FaceScan (Web): sending verify_id ID check (attempt ${attempt}/2, size=${(processedFile.size / (1024*1024)).toFixed(2)} MB)...`);
        
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 45000);

        const ocrRes = await fetch('https://walk-versus-peculiar.ngrok-free.dev/verify_id', {
          method: 'POST',
          headers: { 'ngrok-skip-browser-warning': '69420' },
          body: ocrFd,
          signal: controller.signal
        });
        clearTimeout(timeoutId);

        const durationMs = Math.round(performance.now() - startTime);
        console.log(`FaceScan (Web): verify_id response status=${ocrRes.status} in ${durationMs}ms`);

        if (ocrRes.ok) {
          ocrData = await ocrRes.json();
          break;
        } else {
          lastError = new Error(`Server returned status ${ocrRes.status}`);
        }
      } catch (e) {
        lastError = e;
        console.warn(`FaceScan (Web): verify_id attempt ${attempt} notice:`, e);
        if (attempt < 2) {
          await new Promise(r => setTimeout(r, 600));
        }
      }
    }

    if (!ocrData) {
      console.error("FaceScan (Web): ID verification failed after retries:", lastError);
      setErrors({ ...errors, idImage: 'Verification service is busy. Please try again.' });
      isUploadingRef.current = false;
      setIsUploading(false);
      return;
    }

    if (ocrData.success === false || ocrData.match === false) {
      setErrors({ ...errors, idImage: ocrData.message || ocrData.error || 'Verification failed. Please ensure details are clear.' });
      isUploadingRef.current = false;
      setIsUploading(false);
      return; // reject upload
    }

    // Standardize ID hash & Name hash computation
    const fnClean = (formData.firstName || '').trim().replace(/[^a-zA-Z]/g, '').toUpperCase();
    const lnClean = (formData.lastName || '').trim().replace(/[^a-zA-Z]/g, '').toUpperCase();
    const nameHash = (fnClean && lnClean) ? CryptoJS.SHA256(`${fnClean}_${lnClean}`).toString() : null;

    let computedIdHash = ocrData?.id_hash;
    const extractedNum = ocrData?.id_number || null;
    if (!computedIdHash && extractedNum) {
      const cleanNum = extractedNum.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
      if (cleanNum.length >= 5) {
        computedIdHash = CryptoJS.SHA256(cleanNum).toString();
      }
    }
    if (!computedIdHash) {
      computedIdHash = nameHash;
    }

    // Immediate check in id_registry to block duplicate IDs before upload
    const hashesToCheck = new Set();
    if (computedIdHash) hashesToCheck.add(computedIdHash);
    if (nameHash) hashesToCheck.add(nameHash);

    let isDuplicate = false;
    for (const h of hashesToCheck) {
      try {
        const registryRef = ref(db, `id_registry/${h}`);
        const registrySnap = await get(registryRef);
        if (registrySnap.exists()) {
          const existingUid = registrySnap.val();
          if (!socialUser || existingUid !== socialUser.uid) {
            isDuplicate = true;
            break;
          }
        }
      } catch (err) {
        console.warn('id_registry early check notice:', err);
      }
    }

    if (isDuplicate) {
      setErrors({ ...errors, idImage: 'This ID is already registered to another account.' });
      isUploadingRef.current = false;
      setIsUploading(false);
      return;
    }
    
    const cloudName = 'dnv6ezitm';
    const uploadPreset = 'resort_unsigned';

    const url = `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`;
    const fd = new FormData();
    fd.append('upload_preset', uploadPreset);
    fd.append('file', processedFile);

    try {
      const response = await fetch(url, {
        method: 'POST',
        body: fd
      });
      const data = await response.json();
      if (response.ok) {
        setIdImageUrl(data.secure_url);
        setUnverifiedIdPreview(null);
        setVerifiedFirstName((formData.firstName || '').trim());
        setVerifiedMiddleName((formData.middleName || '').trim());
        setVerifiedLastName((formData.lastName || '').trim());

        setIdHash(computedIdHash || null);
        setExtractedIdNumber(extractedNum);
      } else {
        throw new Error(data.error?.message || 'Upload failed');
      }
    } catch (e) {
      setErrors({ ...errors, idImage: 'Failed to upload image. Please try again.' });
      setVerifiedFirstName(null);
      setVerifiedMiddleName(null);
      setVerifiedLastName(null);
      setIdHash(null);
      setExtractedIdNumber(null);
    } finally {
      isUploadingRef.current = false;
      setIsUploading(false);
    }
  };

  const [selfieReviewPrompt, setSelfieReviewPrompt] = useState(null);
  const [pendingSelfieFile, setPendingSelfieFile] = useState(null);

  const uploadSelfieImage = async (file, forceManualReview = false) => {
    if (!file) return;
    if (isUploadingSelfieRef.current) return;
    isUploadingSelfieRef.current = true;
    setIsUploadingSelfie(true);
    setErrors({ ...errors, selfieImage: null });
    
    // Compress selfie
    const processedSelfie = await compressImage(file);

    // Check Face Match with ID
    if (idImageFile && !forceManualReview) {
      const processedId = await compressImage(idImageFile);
      const startTime = performance.now();
      let ocrData = null;
      let lastError = null;

      for (let attempt = 1; attempt <= 2; attempt++) {
        try {
          console.log(`FaceScan (Web): starting verify_id face match (attempt ${attempt}/2)...`);
          const ocrFd = new FormData();
          ocrFd.append('image', processedId);
          ocrFd.append('selfie', processedSelfie);
          ocrFd.append('firstName', formData.firstName || '');
          ocrFd.append('middleName', formData.middleName || '');
          ocrFd.append('lastName', formData.lastName || '');
          ocrFd.append('idType', (formData.idType || 'Government ID').trim());
          
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 50000);

          const ocrRes = await fetch('https://walk-versus-peculiar.ngrok-free.dev/verify_id', {
            method: 'POST',
            headers: { 'ngrok-skip-browser-warning': '69420' },
            body: ocrFd,
            signal: controller.signal
          });
          clearTimeout(timeoutId);

          const durationMs = Math.round(performance.now() - startTime);
          console.log(`FaceScan (Web): verify_id response status=${ocrRes.status} in ${durationMs}ms`);

          if (ocrRes.ok) {
            ocrData = await ocrRes.json();
            break;
          } else {
            lastError = new Error(`Server returned status ${ocrRes.status}`);
          }
        } catch (e) {
          lastError = e;
          console.warn(`FaceScan (Web): face match attempt ${attempt} error:`, e);
          if (attempt < 2) {
            await new Promise(r => setTimeout(r, 600));
          }
        }
      }

      if (!ocrData) {
        console.warn("FaceScan (Web): connection/timeout notice after retries:", lastError);
        setPendingSelfieFile(processedSelfie);
        setSelfieReviewPrompt('Verification service is busy. Please try again.');
        isUploadingSelfieRef.current = false;
        setIsUploadingSelfie(false);
        return;
      }

      console.log("FaceScan (Web): verify_id result:", ocrData);

      if (ocrData.success === false || ocrData.match === false) {
        const rawMsg = (ocrData.message || '').toLowerCase();
        let promptMsg = 'Face verification could not be completed.';
        if (rawMsg.includes('blurry') || rawMsg.includes('dark')) {
          promptMsg = 'Photo is too dark or blurry. Please retake in better light.';
        } else if (rawMsg.includes('not clearly detected') || rawMsg.includes('no face')) {
          promptMsg = "We couldn't see your face clearly. Face the camera in good light.";
        } else if (rawMsg.includes('does not closely match') || rawMsg.includes('mismatch')) {
          promptMsg = 'Face does not closely match your ID photo.';
        } else if (ocrData.message) {
          promptMsg = ocrData.message;
        }

        setPendingSelfieFile(processedSelfie);
        setSelfieReviewPrompt(promptMsg);
        isUploadingSelfieRef.current = false;
        setIsUploadingSelfie(false);
        return;
      }
    }

    const cloudName = 'dnv6ezitm';
    const uploadPreset = 'resort_unsigned';

    const url = `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`;
    const fd = new FormData();
    fd.append('upload_preset', uploadPreset);
    fd.append('file', file);

    try {
      const response = await fetch(url, {
        method: 'POST',
        body: fd
      });
      const data = await response.json();
      if (response.ok) {
        setSelfieImageUrl(data.secure_url);
        setIsAutoVerified(!forceManualReview);
        setSelfieReviewPrompt(null);
        setPendingSelfieFile(null);
      } else {
        throw new Error(data.error?.message || 'Upload failed');
      }
    } catch (e) {
      setErrors({ ...errors, selfieImage: 'Failed to upload image. Please try again.' });
    } finally {
      isUploadingSelfieRef.current = false;
      setIsUploadingSelfie(false);
    }
  };

  const handleNextStep = () => {
    const validationErrors = validate();
    if (validationErrors && Object.keys(validationErrors).filter(k => k !== 'idType' && k !== 'otherIdType' && k !== 'idImage' && k !== 'selfieImage').length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (validationErrors) {
      setErrors(validationErrors);
      return;
    }

    if (
      !verifiedFirstName ||
      !verifiedLastName ||
      verifiedFirstName !== (formData.firstName || '').trim() ||
      (verifiedMiddleName !== null && verifiedMiddleName !== (formData.middleName || '').trim()) ||
      verifiedLastName !== (formData.lastName || '').trim()
    ) {
      setErrors({
        idImage: 'The name entered does not match the name verified on your ID. Please upload your valid ID again.'
      });
      return;
    }

    setErrors({});
    setLoading(true);
    const claimedIdHashes = [];

    try {
      const effectiveIdType = (formData.idType || 'Government ID').trim();

      // Check whether ID or Name hash is already registered to another account
      const fnClean = (formData.firstName || '').trim().replace(/[^a-zA-Z]/g, '').toUpperCase();
      const lnClean = (formData.lastName || '').trim().replace(/[^a-zA-Z]/g, '').toUpperCase();
      const nameHash = (fnClean && lnClean) ? CryptoJS.SHA256(`${fnClean}_${lnClean}`).toString() : null;

      let primaryHash = idHash;
      if (!primaryHash && extractedIdNumber) {
        const cleanNum = extractedIdNumber.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
        if (cleanNum.length >= 5) {
          primaryHash = CryptoJS.SHA256(cleanNum).toString();
        }
      }
      if (!primaryHash) {
        primaryHash = nameHash;
      }

      const hashesToCheck = new Set();
      if (primaryHash) hashesToCheck.add(primaryHash);
      if (nameHash) hashesToCheck.add(nameHash);

      for (const h of hashesToCheck) {
        try {
          const registryRef = ref(db, `id_registry/${h}`);
          const registrySnap = await get(registryRef);
          if (registrySnap.exists()) {
            const existingUid = registrySnap.val();
            // If exists and doesn't belong to current completing social user
            if (!socialUser || existingUid !== socialUser.uid) {
              setErrors({
                idImage: 'This ID is already registered to another account.'
              });
              setLoading(false);
              return;
            }
          }
        } catch (regErr) {
          console.warn('id_registry read notice:', regErr);
        }
      }

      let user = socialUser;
      if (!isCompletingSocial) {
        const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
        user = userCredential.user;

        await sendEmailVerification(user);

        await updateProfile(user, {
          displayName: `${formData.firstName} ${formData.lastName}`
        });
      }

      // Claim both ID & Name hashes atomically in id_registry
      if (user?.uid) {
        for (const h of hashesToCheck) {
          try {
            const registryRef = ref(db, `id_registry/${h}`);
            await set(registryRef, user.uid);
            claimedIdHashes.push(h);
          } catch (claimErr) {
            console.warn('Failed to claim id_registry for ' + h + ':', claimErr);
          }
        }
      }

      const customId = generateCustomId();

      const userRef = ref(db, `users/${user.uid}`);
      let existingData = {};
      if (isCompletingSocial) {
        const snap = await get(userRef);
        if (snap.exists()) {
          existingData = snap.val();
        }
      }

      await set(userRef, {
        ...existingData,
        firstName: formData.firstName,
        middleName: formData.middleName,
        lastName: formData.lastName,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        role: existingData.role || 'Tourist',
        uid: user.uid,
        customId: existingData.customId || customId,
        isBanned: existingData.isBanned || false,
        createdAt: existingData.createdAt || Date.now(),
        idType: effectiveIdType,
        idImageUrl: idImageUrl,
        idPhotoUrl: idImageUrl, // Both field names for compatibility
        selfieUrl: selfieImageUrl,
        idHash: primaryHash || null,
        idVerified: isAutoVerified,
        identityStatus: isAutoVerified ? 'approved' : 'pending',
        faceStatus: isAutoVerified ? 'approved' : 'pending_review'
      });

      // Welcome Coupon: 10% off room only on successful registration
      try {
        await set(ref(db, `user_coupons/${user.uid}/WELCOME10`), {
          code: 'WELCOME10',
          title: 'Welcome 10% Off',
          description: '10% discount on room reservation upon registration',
          discountType: 'percentage',
          discountValue: 10,
          roomOnly: true,
          active: true,
          applicableRooms: ['ALL'],
          used: false,
          createdAt: Date.now()
        });
      } catch (couponErr) {
        console.warn('Could not assign welcome coupon:', couponErr);
      }

      // Unified EmailJS Welcome Trigger
      if (formData.email) {
        sendWelcomeEmail({
          toEmail: formData.email,
          toName: `${formData.firstName} ${formData.lastName}`.trim(),
          customId: customId,
          role: existingData.role || 'Tourist'
        }).catch(err => console.warn('[EmailJS] Welcome email error:', err));
      }

      alert('Registration Successful! Please check your email to verify your account.');
      sessionStorage.removeItem('registerDraft');
      sessionStorage.removeItem('registerStep');
      onBackToLogin();
    } catch (err) {
      if (claimedIdHashes.length > 0) {
        for (const h of claimedIdHashes) {
          try {
            await remove(ref(db, `id_registry/${h}`));
          } catch (delErr) {
            console.warn('Could not release claimed id hash:', delErr);
          }
        }
      }
      setErrors({ global: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleSocialRegister = async (providerName) => {
    setErrors({});
    setLoading(true);
    let provider;
    if (providerName === 'google') {
      provider = new GoogleAuthProvider();
    } else if (providerName === 'facebook') {
      provider = new FacebookAuthProvider();
    }

    try {
      sessionStorage.setItem('socialSource', 'register');
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      const userRef = ref(db, `users/${user.uid}`);
      const snapshot = await get(userRef);

      if (!snapshot.exists()) {
        // App.js will detect missing profile and route to complete registration
      }
    } catch (err) {
      setErrors({ global: err.message || 'An error occurred during social registration.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', width: '100%',
      backgroundImage: 'linear-gradient(rgba(0,15,8,0.7), rgba(0,15,8,0.7)), url("https://images.unsplash.com/photo-1540541338287-41700207dee6?ixlib=rb-4.0.3&auto=format&fit=crop&w=1470&q=80")',
      backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed', padding: '40px 20px',
    }}>
      <div className="card view-transition" style={{
        width: '100%', maxWidth: '520px', padding: '48px 40px',
        backgroundColor: 'var(--surface)',
        borderRadius: '32px', boxShadow: '0 30px 60px -12px rgba(0,0,0,0.5)',
        border: '1px solid var(--border)', position: 'relative', overflow: 'hidden'
      }}>
        {/* Accent Bar */}
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: '6px',
          background: 'linear-gradient(to right, var(--secondary), var(--primary))'
        }}></div>

        <button
          onClick={() => {
            if (step === 2) {
              setStep(1);
            } else {
              onBackToLogin();
            }
          }}
          style={{
            background: 'none', border: 'none', cursor: 'pointer', display: 'flex',
            alignItems: 'center', gap: '8px', color: 'var(--text-muted)',
            marginBottom: '32px', fontWeight: 700, fontSize: '14px'
          }}
        >
          <ArrowLeft size={18} /> {step === 2 ? 'Back to Details' : 'Back to Login'}
        </button>

        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          <div onClick={onGoHome} style={{ cursor: 'pointer', display: 'inline-block' }}>
            <img src={logo} alt="Resort Connect Logo" style={{ width: '280px', height: 'auto', marginBottom: '16px' }} />
          </div>
          <h2 style={{ margin: 0, fontSize: '26px', fontWeight: 800, color: 'var(--text-main)' }}>
            {step === 1 ? 'Join Resort Connect' : 'Identity Verification'}
          </h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginTop: '4px', fontWeight: 500 }}>
            {step === 1 ? 'Start your premium stay experience' : 'Step 2: Upload a valid government ID'}
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          {step === 1 ? (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                <div className="form-group">
                  <label className="input-label">First Name</label>
                  <div style={{ position: 'relative' }}>
                    <User style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: 'var(--secondary)' }} size={18} />
                    <input type="text"
                      className="input" style={{ paddingLeft: '48px', borderColor: errors.firstName ? '#ef4444' : undefined }} placeholder="Jane"
                      value={formData.firstName} onChange={(e) => handleNameChange('firstName', e.target.value)}
                      maxLength="30"
                    />
                  </div>
                  {errors.firstName && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '6px', fontWeight: 600 }}>⬆ {errors.firstName}</div>}
                </div>
                <div className="form-group">
                  <label className="input-label">Middle Name</label>
                  <div style={{ position: 'relative' }}>
                    <User style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} size={18} />
                    <input
                      className="input" style={{ paddingLeft: '48px', borderColor: errors.middleName ? '#ef4444' : undefined }} placeholder="Optional"
                      value={formData.middleName} onChange={(e) => { setFormData({ ...formData, middleName: handleEmojiFilter(e.target.value) }); setErrors({ ...errors, middleName: null }); }}
                      maxLength="30"
                    />
                  </div>
                  {errors.middleName && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '6px', fontWeight: 600 }}>⬆ {errors.middleName}</div>}
                </div>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label className="input-label">Last Name</label>
                <div style={{ position: 'relative' }}>
                  <User style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: 'var(--secondary)' }} size={18} />
                  <input type="text"
                    className="input" style={{ paddingLeft: '48px', borderColor: errors.lastName ? '#ef4444' : undefined }} placeholder="Doe"
                    value={formData.lastName} onChange={(e) => handleNameChange('lastName', e.target.value)}
                    maxLength="30"
                  />
                </div>
                {errors.lastName && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '6px', fontWeight: 600 }}>⬆ {errors.lastName}</div>}
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label className="input-label">Email Address</label>
                <div style={{ position: 'relative' }}>
                  <Mail style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: 'var(--secondary)' }} size={18} />
                  <input
                    type="email" className="input" style={{ paddingLeft: '48px', borderColor: errors.email ? '#ef4444' : undefined }} placeholder="jane@example.com"
                    value={formData.email} onChange={(e) => { setFormData({ ...formData, email: e.target.value.toLowerCase() }); setErrors({ ...errors, email: null }); }}
                  />
                </div>
                {errors.email && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '6px', fontWeight: 600 }}>⬆ {errors.email}</div>}
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label className="input-label">Phone Number</label>
                <div style={{ position: 'relative' }}>
                  <Phone style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: 'var(--secondary)' }} size={18} />
                  <input
                    type="tel" className="input" style={{ paddingLeft: '48px', borderColor: errors.phoneNumber ? '#ef4444' : undefined }} placeholder="09XX XXX XXXX" maxLength="11"
                    value={formData.phoneNumber} onChange={(e) => { setFormData({ ...formData, phoneNumber: e.target.value.replace(/\D/g, '') }); setErrors({ ...errors, phoneNumber: null }); }}
                  />
                </div>
                {errors.phoneNumber && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '6px', fontWeight: 600 }}>⬆ {errors.phoneNumber}</div>}
              </div>

              {!isCompletingSocial && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '32px' }}>
                  <div className="form-group">
                    <label className="input-label">Password</label>
                    <div style={{ position: 'relative' }}>
                      <Lock style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: 'var(--secondary)' }} size={18} />
                      <input
                        type={showPassword ? 'text' : 'password'} className="input" style={{ paddingLeft: '48px', paddingRight: '48px', borderColor: errors.password ? '#ef4444' : undefined }} placeholder="••••••••"
                        value={formData.password} onChange={(e) => { setFormData({ ...formData, password: e.target.value }); setErrors({ ...errors, password: null }); }}
                      />
                      <button type="button" onClick={() => setShowPassword(p => !p)} tabIndex={-1}
                        style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', padding: '4px' }}>
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    {errors.password && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '6px', fontWeight: 600 }}>⬆ {errors.password}</div>}
                  </div>
                  <div className="form-group">
                    <label className="input-label">Confirm</label>
                    <div style={{ position: 'relative' }}>
                      <Lock style={{ position: 'absolute', left: '16px', top: '50%', transform: 'translateY(-50%)', color: 'var(--secondary)' }} size={18} />
                      <input
                        type={showConfirmPassword ? 'text' : 'password'} className="input" style={{ paddingLeft: '48px', paddingRight: '48px', borderColor: errors.confirmPassword ? '#ef4444' : undefined }} placeholder="••••••••"
                        value={formData.confirmPassword} onChange={(e) => { setFormData({ ...formData, confirmPassword: e.target.value }); setErrors({ ...errors, confirmPassword: null }); }}
                      />
                      <button type="button" onClick={() => setShowConfirmPassword(p => !p)} tabIndex={-1}
                        style={{ position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', padding: '4px' }}>
                        {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                    {errors.confirmPassword && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '6px', fontWeight: 600 }}>⬆ {errors.confirmPassword}</div>}
                  </div>
                </div>
              )}

              <button
                type="button"
                className="btn btn-primary"
                style={{ width: '100%', height: '56px', fontSize: '16px', marginBottom: '16px' }}
                onClick={handleNextStep}
              >
                CONTINUE <ArrowRight size={18} />
              </button>

              {!isCompletingSocial && (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', margin: '24px 0' }}>
                    <div style={{ flex: 1, height: '1px', background: 'var(--border)' }}></div>
                    <span style={{ padding: '0 16px', color: 'var(--text-muted)', fontSize: '12px', fontWeight: 600 }}>OR REGISTER WITH</span>
                    <div style={{ flex: 1, height: '1px', background: 'var(--border)' }}></div>
                  </div>

                  <div style={{ display: 'flex', gap: '16px', marginBottom: '20px' }}>
                    <button
                      type="button"
                      onClick={() => handleSocialRegister('google')}
                      style={{
                        flex: 1, height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                        background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '12px',
                        color: 'var(--text-main)', fontSize: '14px', fontWeight: 600, cursor: 'pointer', transition: 'var(--transition)'
                      }}
                      onMouseOver={e => e.currentTarget.style.background = 'var(--light-bg)'}
                      onMouseOut={e => e.currentTarget.style.background = 'var(--surface)'}
                    >
                      <img src="https://www.svgrepo.com/show/475656/google-color.svg" alt="Google" style={{ width: '20px', height: '20px' }} />
                      Google
                    </button>
                    <button
                      type="button"
                      onClick={() => handleSocialRegister('facebook')}
                      style={{
                        flex: 1, height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                        background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '12px',
                        color: 'var(--text-main)', fontSize: '14px', fontWeight: 600, cursor: 'pointer', transition: 'var(--transition)'
                      }}
                      onMouseOver={e => e.currentTarget.style.background = 'var(--light-bg)'}
                      onMouseOut={e => e.currentTarget.style.background = 'var(--surface)'}
                    >
                      <img src="https://www.svgrepo.com/show/475647/facebook-color.svg" alt="Facebook" style={{ width: '20px', height: '20px' }} />
                      Facebook
                    </button>
                  </div>
                </>
              )}
            </>
          ) : (
            <>
              <div style={{ marginBottom: '32px' }}>
                <label className="input-label">Upload Valid Government ID</label>
                <div style={{
                  border: `2px dashed ${idImageUrl ? '#10B981' : 'var(--border)'}`,
                  borderRadius: '16px',
                  padding: '32px 20px',
                  textAlign: 'center',
                  background: 'var(--light-bg)',
                  cursor: 'pointer',
                  position: 'relative'
                }}>
                  {idImageUrl ? (
                    <div>
                      <img src={idImageUrl} alt="ID Preview" style={{ maxWidth: '100%', maxHeight: '160px', objectFit: 'contain', borderRadius: '8px', marginBottom: '12px' }} />
                      <p style={{ color: '#10B981', fontWeight: 700, margin: 0 }}>ID Uploaded Successfully</p>
                      <button
                        type="button"
                        onClick={() => setIdImageUrl(null)}
                        style={{ background: 'none', border: 'none', color: '#EF4444', fontSize: '13px', fontWeight: 700, marginTop: '8px', cursor: 'pointer' }}
                      >
                        Remove & Replace
                      </button>
                    </div>
                  ) : (
                    <div>
                      {isUploading ? (
                        <div className="loader" style={{ margin: '0 auto' }}></div>
                      ) : unverifiedIdPreview ? (
                        <div style={{ position: 'relative', display: 'inline-block' }}>
                          <img src={unverifiedIdPreview} alt="Unverified ID Preview" style={{ maxWidth: '100%', maxHeight: '160px', objectFit: 'contain', borderRadius: '8px', opacity: 0.8 }} />
                          <div style={{ marginTop: '10px' }}>
                            <p style={{ color: '#EF4444', fontWeight: 600, fontSize: '12px', margin: '0 0 8px' }}>Verification did not pass</p>
                            <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', position: 'relative', zIndex: 20 }}>
                              {idImageFile && (
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    uploadIdImage(idImageFile);
                                  }}
                                  style={{
                                    padding: '6px 14px',
                                    borderRadius: '8px',
                                    background: 'var(--primary)',
                                    color: 'white',
                                    border: 'none',
                                    fontSize: '12px',
                                    fontWeight: 700,
                                    cursor: 'pointer'
                                  }}
                                >
                                  Retry with Updated Name
                                </button>
                              )}
                              <span style={{ display: 'inline-block', padding: '6px 14px', borderRadius: '8px', background: 'var(--surface)', border: '1px solid var(--border)', fontSize: '12px', fontWeight: 700, color: 'var(--text-main)', pointerEvents: 'none' }}>
                                Retake Photo
                              </span>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <>
                          <User size={48} color="var(--text-muted)" style={{ margin: '0 auto 12px', opacity: 0.5 }} />
                          <p style={{ color: 'var(--text-main)', fontWeight: 600, margin: '0 0 8px 0' }}>Click to upload front of ID</p>
                          <p style={{ color: 'var(--text-muted)', fontSize: '12px', margin: 0 }}>Landscape & Portrait accepted</p>
                        </>
                      )}
                    </div>
                  )}
                  {!idImageUrl && !isUploading && (
                    <input
                      type="file"
                      accept="image/*"
                      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer', zIndex: 10 }}
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setIdImageFile(file);
                          uploadIdImage(file);
                        }
                        e.target.value = null;
                      }}
                    />
                  )}
                </div>
                {errors.idImage && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '6px', fontWeight: 600 }}>⬆ {errors.idImage}</div>}
              </div>

              <div style={{ marginBottom: '32px' }}>
                <label className="input-label">Scan Face / Selfie</label>
                <div style={{
                  border: `2px dashed ${selfieImageUrl ? '#10B981' : 'var(--border)'}`,
                  borderRadius: '16px',
                  padding: '32px 20px',
                  textAlign: 'center',
                  background: 'var(--light-bg)',
                  cursor: 'pointer',
                  position: 'relative'
                }}>
                  {selfieImageUrl ? (
                    <div>
                      <img src={selfieImageUrl} alt="Selfie Preview" style={{ maxWidth: '100%', maxHeight: '160px', objectFit: 'contain', borderRadius: '8px', marginBottom: '12px' }} />
                      <p style={{ color: '#10B981', fontWeight: 700, margin: 0 }}>Selfie Uploaded Successfully</p>
                      {isAutoVerified && <p style={{ color: '#3B82F6', fontWeight: 700, fontSize: '12px', marginTop: '4px' }}>AI LIVENESS VERIFIED</p>}
                      <button
                        type="button"
                        onClick={() => setSelfieImageUrl(null)}
                        style={{ background: 'none', border: 'none', color: '#EF4444', fontSize: '13px', fontWeight: 700, marginTop: '8px', cursor: 'pointer' }}
                      >
                        Remove & Replace
                      </button>
                    </div>
                  ) : showWebcam ? (
                    <div style={{ position: 'relative', zIndex: 10 }}>
                      <div style={{ position: 'relative', overflow: 'hidden', borderRadius: '50%', width: '200px', height: '200px', margin: '0 auto', background: '#000', border: '4px solid #10B981' }}>
                        <video ref={videoRef} autoPlay playsInline muted style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)' }} />
                        <div style={{ position: 'absolute', bottom: '20px', left: '0', right: '0', textAlign: 'center' }}>
                          <span style={{ background: 'rgba(0,0,0,0.6)', color: 'white', padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: 700 }}>
                            {faceDetectionStatus}
                          </span>
                        </div>
                      </div>
                      <canvas ref={canvasRef} style={{ display: 'none' }} />
                      <div style={{ display: 'flex', gap: '8px', marginTop: '16px', justifyContent: 'center' }}>
                        {isModelLoading && <div className="loader" style={{ width: '20px', height: '20px' }}></div>}
                        {(faceDetectionStatus.includes("Face detected") || showManualCaptureBtn) && (
                          <button type="button" onClick={captureWebcam} style={{ padding: '8px 24px', background: '#10B981', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '13px' }}>
                            {showManualCaptureBtn ? "Take Photo" : "Capture"}
                          </button>
                        )}
                        <button type="button" onClick={stopWebcam} style={{ padding: '8px 24px', background: '#EF4444', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '13px' }}>Cancel Camera</button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ position: 'relative' }}>
                      {isUploadingSelfie ? (
                        <div className="loader" style={{ margin: '0 auto' }}></div>
                      ) : (
                        <>
                          <User size={48} color="var(--text-muted)" style={{ margin: '0 auto 12px', opacity: 0.5 }} />
                          <p style={{ color: 'var(--text-main)', fontWeight: 600, margin: '0 0 8px 0' }}>Scan your face for auto-verification</p>
                          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '12px', position: 'relative', zIndex: 10 }}>
                            {!idImageUrl ? (
                              <p style={{ color: '#EF4444', fontSize: '12px', fontWeight: 600, background: '#FEF2F2', padding: '8px 16px', borderRadius: '8px', border: '1px solid #FCA5A5' }}>Please upload a valid ID successfully first.</p>
                            ) : (
                              <button type="button" onClick={startWebcam} style={{ padding: '8px 16px', background: 'var(--primary)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontSize: '12px' }}>Open Camera for Verification</button>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
                {errors.selfieImage && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '6px', fontWeight: 600 }}>⬆ {errors.selfieImage}</div>}

                {selfieReviewPrompt && (
                  <div style={{
                    marginTop: '12px',
                    padding: '16px',
                    borderRadius: '12px',
                    background: 'var(--surface)',
                    border: '1.5px solid #F59E0B',
                    boxShadow: 'var(--shadow)'
                  }}>
                    <p style={{ margin: '0 0 10px', fontSize: '13px', color: 'var(--text-main)', fontWeight: 600 }}>
                      ⚠️ {selfieReviewPrompt}
                    </p>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        onClick={() => {
                          setSelfieReviewPrompt(null);
                          setPendingSelfieFile(null);
                          startWebcam();
                        }}
                        style={{
                          padding: '8px 16px',
                          borderRadius: '8px',
                          background: 'var(--light-bg)',
                          border: '1px solid var(--border)',
                          color: 'var(--text-main)',
                          fontWeight: 700,
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        Try Again
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (pendingSelfieFile) {
                            uploadSelfieImage(pendingSelfieFile, true);
                          }
                        }}
                        style={{
                          padding: '8px 16px',
                          borderRadius: '8px',
                          background: 'var(--primary)',
                          border: 'none',
                          color: 'white',
                          fontWeight: 700,
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        Continue for Manual Review
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  style={{ flex: 1, height: '56px', fontSize: '16px' }}
                  onClick={() => {
                    if (idImageUrl || selfieImageUrl) {
                      setIdImageUrl(null);
                      setSelfieImageUrl(null);
                      setVerifiedFirstName(null);
                      setVerifiedLastName(null);
                      setIsAutoVerified(false);
                      setShowWebcam(false);
                      if (videoRef.current && videoRef.current.srcObject) {
                        videoRef.current.srcObject.getTracks().forEach(track => track.stop());
                      }
                    }
                    setStep(1);
                  }}
                  disabled={loading || isUploading || isUploadingSelfie}
                >
                  BACK
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  style={{ flex: 1, height: '56px', fontSize: '16px' }}
                  disabled={loading || isUploading || isUploadingSelfie}
                >
                  {loading ? <div className="loader" style={{ width: '20px', height: '20px', borderTopColor: 'white' }}></div> : 'CREATE ACCOUNT'}
                </button>
              </div>
            </>
          )}

          {errors.global && (
            <div style={{
              backgroundColor: '#EF4444', color: 'white', padding: '16px',
              borderRadius: '12px', fontSize: '14px', marginBottom: '24px',
              textAlign: 'center', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px'
            }}>
              <ShieldCheck size={20} /> {errors.global}
            </div>
          )}
          <p style={{ textAlign: 'center', marginTop: '32px', fontSize: '13px', color: 'var(--text-muted)', fontWeight: 500 }}>
            By registering, you agree to our <strong>Terms</strong> and <strong>Privacy Policy</strong>.
          </p>
        </form>
      </div>
      <style>{`
        .input-label { display: block; font-size: 12px; font-weight: 800; color: var(--text-main); margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.5px; }
        .view-transition { animation: fadeIn 0.4s ease-out; }
      `}</style>
    </div>
  );
};

export default Register;
